import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback
} from 'react';
import styled from 'styled-components';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import debounceFunction from 'lodash.debounce';
import { MentionsInput, Mention } from 'react-mentions';
import UserAvatar from '../UserAvatar/UserAvatar';
import Icon from '../Icon/Icon';

import './CommentInput.css';
import mentioningInlineStyles from './MentioningInlineStyles';
import MentionListItem from '../MentionListItem/MentionListItem';
import {
  MENTION_INPUT_MARKUP,
  renderMentionedUserInInput
} from '../../utils/mentioning/mentionFormatters';
import {
  getMenuMessageKey,
  PROTECTED_COMMUNITY
} from './MentioningMenuUtilities';

const StyledCommentInput = styled.div`
  ul::before {
    background-color: #d8d8d8;
    color: #4a4a4a;
    content: '${(props) => props.mentioningMessage}';
    font-size: 12px;
    font-weight: 600;
    display: block;
    padding: 4px 16px;
  }
`;

/**
 * Function to calculate the rows required for the text area based on style data.
 * Removes the vertical padding from a (scroll) height, checks the number of lines contained in this via the line height, before checking this against a maximum.
 * Exported so it can be unit tested since we can't simulate the height of the heightGuage in unit tests.
 * @param {number} scrollHeight - The scrollHeight of content if rendered at the available width on the page.
 * @param {number} lineHeight  - The height of a single row (font plus space between lines)
 * @param {number} maxRows  - The maximum number of rows allowed.
 * @param {number} padding - The vertical padding of the element.
 * @param {number} [rowAdjustment=1] - Optional param to ajust the rows (can be a negative number), adjustment is added before the maximum check.
 * @returns {number} - The number of rows required, if this exceeds the maximum the maximum will be returned. Adjustment is added before the maximum check.
 */
export const calculateRows = (
  scrollHeight,
  lineHeight,
  maxRows,
  padding,
  rowAdjustment = 0
) => {
  const adjusted = scrollHeight - padding;
  const rows = Math.ceil(adjusted / lineHeight) + rowAdjustment;
  return rows >= maxRows ? maxRows : rows;
};

// The trickiest part of this component was the variable height based on content, whilst also maintaining a maximum row limit.
// This isn't a feature of text areas currently :( There were two ways I could think of doing this:
// 1. Control the height of the text area.
// 2. Control the rows property of the text area.
// (well 3 ways if you consider using a third party library as an approach).
// Out of the two, I preferred the rows approach as it seems a little more data focused/testable.
// Either of these approaches requires calculating the rows/height required at some point.
// For this I create an invisible, identically styled element that contains the text entered.
// By checking the scroll height of this element we can calculate rows (or height) required for our actual text area.
// One problem with this approach is that it intrinsically needs to know the line-height of the element to calc rows
// (I did test pulling out the styles onMount but it wasn't very reliable, also probably not the best performance - anyway I thought it prudent to make it a prop).
const CommentInput = ({
  userId,
  avatar,
  placeHolder,
  maxRows,
  verticalPadding,
  lineHeight,
  onTextChange,
  onFocusChange,
  commentText,
  hasError,
  handleRequestMentionableUsers,
  communityType
}) => {
  const heightGuageRef = useRef(); // hidden element used to track height
  const [textAreaRows, setTextAreaRows] = useState(1); // rows property for the text input.
  const [hasFocus, setHasFocus] = useState(false);

  // Using a ref to store a mutable value
  // (in this case the correct translation key for
  // the required message)
  const mentioningMessageKey = useRef('');
  /**
   * Function to handle checking the rows in the heightGuage element and setting the textAreaRows state variable accordingly.
   * Makes allowances for the last character being a line break.
   */
  const processHeight = () => {
    const currentGuage = heightGuageRef && heightGuageRef.current;
    const currentGuageHeight = currentGuage && currentGuage.scrollHeight;
    const isNewLineChar = /\r|\n$/.test(commentText);
    const newRows = calculateRows(
      currentGuageHeight,
      lineHeight,
      maxRows,
      verticalPadding,
      isNewLineChar ? 1 : 0
    );
    // only cause a render cycle if change in rows has occured.
    if (textAreaRows !== newRows) {
      setTextAreaRows(newRows);
    }
  };

  const debouncedResize = debounceFunction(processHeight, 100);

  // on mount/dismount functions
  useEffect(() => {
    // resize check on rows is required as responsive is a requirement and wordwrap alters rows.
    window.addEventListener('resize', debouncedResize);
    return () => {
      // cleanup
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  useEffect(() => {
    mentioningMessageKey.current = getMenuMessageKey(communityType);
  }, [communityType]);

  // useLayoutEffect effect is better in this instance since we are reacting to a change in the state before checking the height gauge element/running calcs then making another screen adjustment.
  // useLayoutEffect will help us avoid seeing the change in the text area rendered to the screen before the rows have been adjusted (would cause flickering in the UI).
  useLayoutEffect(() => {
    processHeight();
  }, [commentText]);

  /**
   * Memoized callback function for focus/blur off the text area.
   * @param {object} evnt - The text area focus/blur event.
   */
  const memoizedFocusCallBack = useCallback((evnt) => {
    const _hasFocus = evnt.type === 'focus';
    setHasFocus(_hasFocus);
    onFocusChange(_hasFocus);
  }, []);

  const isProtectedCommunity = communityType === PROTECTED_COMMUNITY;

  return (
    <StyledCommentInput
      className={`comment-input ${hasFocus ? 'has-focus' : ''}`}
      styleName="container"
      mentioningMessage={i18next.t(mentioningMessageKey.current)}
    >
      {avatar && <UserAvatar userId={userId} avatar={avatar} authorIsUser />}
      <div styleName="input_wrapper">
        <span styleName="height_guage" ref={heightGuageRef}>
          {commentText}
        </span>
        <MentionsInput
          rows={textAreaRows}
          styleName={`text_area ${hasError ? 'text_area-error' : ''}`}
          value={commentText}
          onChange={onTextChange}
          onFocus={memoizedFocusCallBack}
          onBlur={memoizedFocusCallBack}
          tabIndex={0}
          style={mentioningInlineStyles}
          allowSpaceInQuery
        >
          <Mention
            styleName="mention"
            data={handleRequestMentionableUsers}
            renderSuggestion={(suggestion) => (
              <MentionListItem
                {...suggestion}
                showInviteMessage={isProtectedCommunity}
              />
            )}
            displayTransform={renderMentionedUserInInput}
            markup={MENTION_INPUT_MARKUP}
          />
        </MentionsInput>
        <span
          styleName={`placeholder ${commentText?.length === 0 ? 'active' : ''}`}
        >
          {placeHolder}
        </span>
        {hasError && (
          <div styleName="error-icon-container">
            <Icon icon="exclamation-triangle" />
          </div>
        )}
      </div>
    </StyledCommentInput>
  );
};

CommentInput.propTypes = {
  commentText: PropTypes.string.isRequired,
  onTextChange: PropTypes.func.isRequired,
  handleRequestMentionableUsers: PropTypes.func.isRequired,
  avatar: PropTypes.string,
  userId: PropTypes.number,
  maxRows: PropTypes.number,
  lineHeight: PropTypes.number,
  verticalPadding: PropTypes.number,
  placeHolder: PropTypes.string,
  onFocusChange: PropTypes.func,
  hasError: PropTypes.bool,
  communityType: PropTypes.string
};

CommentInput.defaultProps = {
  avatar: null,
  maxRows: 10,
  lineHeight: 20,
  verticalPadding: 18,
  placeHolder: 'Write a comment...',
  onFocusChange: () => {},
  userId: undefined,
  hasError: false,
  communityType: null
};

export default CommentInput;
